import type {
  BossPhase,
  CitySetpieceKind,
  MapDefinition,
  RaceEventMode,
  RaceWagerKind,
  StyleAction,
} from './types.js';

export interface RaceWagerDefinition {
  id: RaceWagerKind;
  name: string;
  description: string;
  target: number;
  xp: number;
}

export const RACE_WAGERS: readonly RaceWagerDefinition[] = [
  {
    id: 'clean-finish',
    name: 'CLEAN ESCAPE',
    description: 'Finish without a damaging crash or manual recovery.',
    target: 1,
    xp: 140,
  },
  {
    id: 'clean-passes',
    name: 'SURGICAL PASSES',
    description: 'Complete three clean overtakes before the flag.',
    target: 3,
    xp: 170,
  },
  {
    id: 'extreme-route',
    name: 'NO SAFE LINE',
    description: 'Complete one authored extreme alternate route.',
    target: 1,
    xp: 190,
  },
] as const;

export function raceWagerDefinition(kind: RaceWagerKind | undefined | null): RaceWagerDefinition | null {
  return RACE_WAGERS.find((wager) => wager.id === kind) ?? null;
}

/** A wager must be achievable under the authored track and event rules. */
export function raceWagerAvailable(
  kind: RaceWagerKind | undefined | null,
  map: Pick<MapDefinition, 'features'>,
  mode: RaceEventMode,
  competitorCount?: number,
): boolean {
  if (!kind || mode === 'time-trial') return false;
  if (kind === 'clean-passes' && competitorCount !== undefined && competitorCount < 2) {
    return false;
  }
  if (kind === 'extreme-route') {
    return (map.features.alternateRoutes ?? []).some((route) => route.risk === 'extreme');
  }
  return true;
}

/** Four readable phases derived from authoritative race state. */
export function bossPhaseFor(
  bossScore: number,
  closestHumanScore: number,
  completedRatio: number,
  elapsedMs: number,
): BossPhase {
  if (elapsedMs < 7_000) return 'intro';
  if (completedRatio >= 0.72) return 'final';
  return bossScore + 0.025 < closestHumanScore ? 'hunt' : 'pressure';
}

export interface SignatureAbilityReward {
  nitro: number;
  boostMs: number;
  repair: number;
}

const NO_SIGNATURE_REWARD: SignatureAbilityReward = { nitro: 0, boostMs: 0, repair: 0 };

/**
 * Mastery abilities are event-triggered sidegrades: they create a short attack
 * window but never alter a vehicle's permanent top speed or grip table.
 */
export function signatureAbilityReward(
  signatureId: string,
  action: Exclude<StyleAction, '' | 'CHAIN LOST'>,
): SignatureAbilityReward {
  switch (signatureId) {
    case 'long-burn':
      return action === 'SLINGSHOT' || action === 'PERFECT LAUNCH'
        ? { nitro: 10, boostMs: 1_100, repair: 0 } : NO_SIGNATURE_REWARD;
    case 'apex-hunter':
      return action === 'PERFECT CORNER'
        ? { nitro: 12, boostMs: 1_350, repair: 0 } : NO_SIGNATURE_REWARD;
    case 'precision-chain':
      return action === 'CLEAN PASS' || action === 'PERFECT CORNER'
        ? { nitro: 8, boostMs: 900, repair: 0 } : NO_SIGNATURE_REWARD;
    case 'smoke-show':
      return action === 'DRIFT BOOST'
        ? { nitro: 14, boostMs: 1_200, repair: 0 } : NO_SIGNATURE_REWARD;
    case 'trailblazer':
      return action === 'SHORTCUT' || action === 'LOOP MASTER'
        ? { nitro: 16, boostMs: 1_300, repair: 0 } : NO_SIGNATURE_REWARD;
    case 'road-presence':
      return action === 'TAKEDOWN' || action === 'CLEAN PASS'
        ? { nitro: 8, boostMs: 800, repair: 6 } : NO_SIGNATURE_REWARD;
    case 'overmatch':
      return action === 'TAKEDOWN'
        ? { nitro: 20, boostMs: 1_600, repair: 10 } : NO_SIGNATURE_REWARD;
    case 'air-attack':
      return action === 'AIR'
        ? { nitro: 16, boostMs: 1_450, repair: 0 } : NO_SIGNATURE_REWARD;
    case 'wild-line':
      return action === 'AIR' || action === 'SHORTCUT'
        ? { nitro: 13, boostMs: 1_250, repair: 0 } : NO_SIGNATURE_REWARD;
    default:
      return action === 'LIVE CONTRACT'
        ? { nitro: 8, boostMs: 700, repair: 0 } : NO_SIGNATURE_REWARD;
  }
}

export interface CitySetpieceProfile {
  name: string;
  warning: string;
  colour: string;
}

const CITY_SETPIECES: Readonly<Record<CitySetpieceKind, CitySetpieceProfile>> = {
  'bern-tram': { name: 'BERN TRAM CROSSING', warning: 'TRAM LINE LIVE', colour: '#e34b45' },
  'luzern-drawbridge': { name: 'CHAPEL BRIDGE SERVICE', warning: 'BRIDGE DECK MOVING', colour: '#e5b76a' },
  'hannover-roadworks': { name: 'MESSE NIGHT WORKS', warning: 'CREW ON THE CIRCUIT', colour: '#ff9f2f' },
  'berlin-convoy': { name: 'CAPITAL CONVOY', warning: 'ESCORT CROSSING', colour: '#5ad7ff' },
};

export function citySetpieceProfile(kind: CitySetpieceKind | undefined): CitySetpieceProfile | null {
  return kind ? CITY_SETPIECES[kind] ?? null : null;
}

export interface DailyChallenge {
  id: string;
  mapId: string;
  carId: string;
  weather: MapDefinition['weather'];
  timeOfDay: MapDefinition['timeOfDay'];
}

/** UTC date keeps the same daily seed for players in every timezone. */
export function dailyChallengeFor(
  maps: readonly Pick<MapDefinition, 'id' | 'weather' | 'timeOfDay'>[],
  carIds: readonly string[],
  date = new Date(),
): DailyChallenge | null {
  // A lone emergency map restored from an offline race is not the published
  // catalogue and must not silently invent a different "global" daily.
  if (maps.length < 2 || carIds.length === 0) return null;
  const orderedMaps = [...maps].sort((first, second) => first.id.localeCompare(second.id));
  const orderedCarIds = [...new Set(carIds)].sort((first, second) => first.localeCompare(second));
  if (orderedCarIds.length === 0) return null;
  const id = date.toISOString().slice(0, 10);
  const map = orderedMaps[hash(`${id}:map`) % orderedMaps.length]!;
  const weatherPool: readonly MapDefinition['weather'][] = ['clear', 'haze', 'rain'];
  const timePool: readonly MapDefinition['timeOfDay'][] = ['dawn', 'day', 'sunset', 'night'];
  return {
    id,
    mapId: map.id,
    carId: orderedCarIds[hash(`${id}:car`) % orderedCarIds.length]!,
    weather: weatherPool[hash(`${id}:weather`) % weatherPool.length]!,
    timeOfDay: timePool[hash(`${id}:time`) % timePool.length]!,
  };
}

function hash(value: string): number {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
}
